import type { ReactNode } from 'react';
import { Pause, Play } from 'lucide-react';
import { HelpAnchor, SurfacePageHeader } from '@/design-system';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { PipelineStrip } from './PipelineStrip';
import type { PipelineCellRead, PipelineStageId } from './PipelineStrip.transform';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { flowClockAtOffset, flowClockOffset, formatFlowClock, ZINC_REPLAY_END, ZINC_REPLAY_START } from './FlowLanding.transform';
import { ValueBand } from './ValueBand';
import type { ValueBandRead } from './ValueBand.transform';
import { CaseMarker } from './CaseMarker';

const HELP_DOC_BY_STAGE: Readonly<Record<PipelineStageId, string>> = {
  'plant-data': 'advisory/flow-landing',
  signals: 'advisory/alarms',
  excursions: 'advisory/alarms',
  cases: 'advisory/flow-landing',
  diagnosis: 'advisory/driver-tree',
  action: 'advisory/approvals',
  value: 'advisory/driver-tree',
  learn: 'advisory/rule-set',
};

const REPLAY_RANGE_LABEL = `${formatDayMonth(ZINC_REPLAY_START)} ${ZINC_REPLAY_START.slice(11, 16)} → ${formatDayMonth(ZINC_REPLAY_END)} ${ZINC_REPLAY_END.slice(11, 16)}`;

function ReplayControl({
  cursor,
  playing,
  holding,
  ticks,
  onToggle,
  onScrub,
}: {
  cursor: SeriesTime;
  playing: boolean;
  holding?: boolean;
  ticks?: readonly number[];
  onToggle: () => void;
  onScrub: (cursor: SeriesTime) => void;
}) {
  const maxOffset = flowClockOffset(ZINC_REPLAY_END);
  const offset = Math.min(maxOffset, flowClockOffset(cursor));
  const clock = formatFlowClock(cursor);
  const clockSplit = clock.lastIndexOf(' · ') + 3;
  const clockDay = clock.slice(0, clockSplit);
  const clockTime = clock.slice(clockSplit);
  const pct = (value: number) => `${Math.max(0, Math.min(100, (value / Math.max(1, maxOffset)) * 100))}%`;

  return (
    <div
      id="walkthrough-flow-replay"
      className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1"
    >
      <span className="whitespace-nowrap text-xs tabular-nums text-ink-secondary" data-replay-clock>
        {clockDay}<span className="font-semibold text-ink-primary">{clockTime}</span>{holding ? ' · hold' : ''}
      </span>
      <div className="flex min-w-0 items-center gap-2.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] py-1 pl-1 pr-3">
        <button
          type="button"
          aria-label={playing ? 'Pause replay' : 'Play replay'}
          title={playing ? 'Pause replay' : 'Play replay'}
          onClick={onToggle}
          className="inline-flex size-[26px] shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] focus-visible:ring-offset-1 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
        >
          {playing ? <Pause className="size-3" fill="currentColor" aria-hidden="true" /> : <Play className="ml-px size-3" fill="currentColor" aria-hidden="true" />}
        </button>
        <span className="relative flex h-[24px] w-32 shrink-0 items-center rounded-full has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-accent-500)] sm:w-44">
          <span aria-hidden="true" className="absolute inset-x-0 h-1 rounded-full bg-[var(--hairline-strong)]" />
          <span aria-hidden="true" className="absolute left-0 h-1 rounded-full bg-ink-primary" style={{ width: pct(offset) }} />
          {(ticks ?? []).map((tick) => (
            <span
              key={tick}
              aria-hidden="true"
              data-replay-tick={tick}
              className="absolute top-1/2 h-2.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--state-warning)]"
              style={{ left: pct(tick) }}
            />
          ))}
          <span aria-hidden="true" className="absolute size-2.5 -translate-x-1/2 rounded-full border-2 border-[var(--surface-2)] bg-ink-primary shadow-sm" style={{ left: pct(offset) }} />
          <input
            type="range"
            aria-label="Replay time"
            data-flow-scrub
            min={0}
            max={maxOffset}
            step={15}
            value={offset}
            onChange={(event) => onScrub(flowClockAtOffset(Number(event.currentTarget.value)))}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </span>
        <span className="hidden whitespace-nowrap text-xs tabular-nums text-muted lg:inline">{REPLAY_RANGE_LABEL}</span>
      </div>
    </div>
  );
}

export function FlowLanding({
  stage,
  cells,
  valueRead,
  projectName,
  cursor,
  playing,
  holding,
  onToggleReplay,
  onScrub,
  marker,
  markers,
  arrivingStage,
  replayTicks,
  onSelectStage,
  showMoney = true,
  children,
}: {
  stage: PipelineStageId;
  cells: Readonly<Record<PipelineStageId, PipelineCellRead>>;
  valueRead: ValueBandRead;
  projectName: string;
  cursor: SeriesTime;
  playing: boolean;
  holding?: boolean;
  onToggleReplay: () => void;
  onScrub: (cursor: SeriesTime) => void;
  marker?: { caseId: string; label: string; stage: PipelineStageId; stack?: number } | null;
  markers?: readonly { caseId: string; label: string; stage: PipelineStageId; offset?: number; stack?: number }[];
  arrivingStage?: PipelineStageId | null;
  /** Replay offsets (minutes from the replay start) where the zinc case changes stage. */
  replayTicks?: readonly number[];
  onSelectStage: (stage: PipelineStageId) => void;
  /** #731: the floor seats read no US$ on the band or the strip. */
  showMoney?: boolean;
  children: ReactNode;
}) {
  // Cases sharing a stage stack below the lane; reserve the space so the lowest
  // token never lands on the panel under the strip.
  const markerLanes = markers
    ? markers.reduce((max, item) => Math.max(max, (item.stack ?? 0) + 1), 0)
    : marker ? 1 : 0;
  return (
    <div className="space-y-3">
      <SurfacePageHeader
        title="Advisory"
        titleSize="large"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ReplayControl
              cursor={cursor}
              playing={playing}
              holding={holding}
              ticks={replayTicks}
              onToggle={onToggleReplay}
              onScrub={onScrub}
            />
            <HelpAnchor doc={HELP_DOC_BY_STAGE[stage]} label="Advisory" />
          </div>
        }
      />
      <ValueBand read={valueRead} projectName={projectName} showMoney={showMoney} />
      <div
        id="walkthrough-flow-strip"
        data-pipeline-strip-root
        className={`relative ${markerLanes > 1 ? 'pb-8' : ''}`}
      >
        <PipelineStrip
          activeStage={stage}
          cells={cells}
          arrivingStage={arrivingStage}
          onSelect={onSelectStage}
        />
        {markers ? markers.map((item) => <CaseMarker key={item.caseId} marker={item} />) : <CaseMarker marker={marker ?? null} />}
      </div>
      <section
        id="pipeline-stage-panel"
        role="tabpanel"
        aria-labelledby={`pipeline-tab-${stage}`}
        data-stage-panel={stage}
        className="min-w-0"
      >
        {children}
      </section>
    </div>
  );
}
